import { z } from "zod";

/**
 * Tenants shared contract (Strangler-Fig migration, manifest-6).
 *
 * Single source of truth for the `tenants` wire shape shared by the web SPA
 * (`src/repositories/tenants.ts`) and the NestJS slice (`apps/api/src/tenants/*`).
 * Shape is **snake_case** so the frontend<->Nest transport is a field-for-field
 * swap (no renames), matching the Supabase generated `tenants` Row and the
 * `tenants_with_status` view (which adds a derived `status`).
 *
 * The web repo `.parse()`s Nest responses against {@link tenantSchema} at the
 * flip boundary, so any backend/frontend drift surfaces at runtime (and the repo
 * falls back to the Supabase path).
 *
 * ⚠️ TOLERANCE RULE (the #1699 silent-no-op lesson): tenants have no joined
 * relations (flat rows), but the LIST query reads the `tenants_with_status`
 * VIEW which appends a derived `status` column the bare `tenants` Row lacks, and
 * future migrations may add columns. So the schema is `.passthrough()` and the
 * derived/optional fields are `.optional()` — a too-strict schema would make
 * every Nest call silently fall back to Supabase. Enum-like columns
 * (`tenant_type`, `id_type`, `notification_channel`, derived `status`) stay open
 * strings so a new value never rejects valid server data.
 */
export const tenantSchema = z
  .object({
    id: z.string(),
    company_id: z.string(),
    tenant_type: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    first_name_en: z.string().nullable().optional(),
    last_name_en: z.string().nullable().optional(),
    commercial_name: z.string().nullable().optional(),
    commercial_name_en: z.string().nullable().optional(),
    unified_number: z.string().nullable().optional(),
    cr_number: z.string().nullable().optional(),
    vat_number: z.string().nullable().optional(),
    phone: z.string(),
    alternative_phone: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    id_type: z.string(),
    id_number: z.string(),
    id_image_url: z.string().nullable().optional(),
    date_of_birth: z.string().nullable().optional(),
    nationality: z.string().nullable().optional(),
    address: z.string().nullable().optional(),
    short_address: z.string().nullable().optional(),
    national_address: z.string().nullable().optional(),
    country: z.string().nullable().optional(),
    region: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    district: z.string().nullable().optional(),
    street_name: z.string().nullable().optional(),
    building_number: z.string().nullable().optional(),
    additional_code: z.string().nullable().optional(),
    postal_code: z.string().nullable().optional(),
    notification_channel: z.string().nullable().optional(),
    user_id: z.string().nullable().optional(),
    invitation_status: z.string().nullable().optional(),
    is_active: z.boolean().nullable(),
    deleted_at: z.string().nullable().optional(),
    created_at: z.string(),
    updated_at: z.string(),
    // Derived by the `tenants_with_status` view (list / stats-enhanced only).
    status: z.string().nullable().optional(),
  })
  // Tolerate extra columns the server may add (new migration columns) without
  // forcing a same-PR schema bump — keeps the flip from silently falling back.
  .passthrough();

export type Tenant = z.infer<typeof tenantSchema>;

/**
 * Aggregated active/inactive stats the staff dashboard consumes. Mirrors
 * `tenantsRepository.stats` (`{ total, active, inactive }`).
 */
export const tenantStatsSchema = z
  .object({
    total: z.number(),
    active: z.number(),
    inactive: z.number(),
  })
  .passthrough();

export type TenantStats = z.infer<typeof tenantStatsSchema>;

/**
 * Minimal enhanced-stats projection (id + derived `status`) the staff dashboard
 * joins with active contracts in-memory. Mirrors `tenantsRepository.statsEnhanced`.
 */
export const tenantStatsEnhancedRowSchema = z
  .object({
    id: z.string(),
    status: z.string().nullable().optional(),
  })
  .passthrough();

export type TenantStatsEnhancedRow = z.infer<typeof tenantStatsEnhancedRowSchema>;
